import React, { useState, useMemo, useEffect } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { Avatar } from "../shared/Avatar";
import { apiCreateGiving, apiCreateExpense, apiListPayees } from "./api";
import { todayLocalISO } from "../../lib/localDate";

// ============================================================
// BulkEntryModal — unified bulk intake for BOTH Giving and Expenses.
//
// Giving mode (default): one shared Date + Payment Method at the
//   top; each row (one per active member, alphabetical, plus a
//   pinned Anonymous row) has its own Amount + Category.
//
// Expenses mode: one shared Date at the top; free-form rows the
//   user adds themselves — each with Payee (autocomplete against
//   the top 50 recently-used payees), Amount, and Category. Payees
//   don't need to be pre-registered.
//
// Rows with zero / blank amounts are skipped on submit. Real-time
// total is shown at the top for both modes.
// ============================================================

const GIVING_CATEGORIES  = ["Regular Offering", "Tithe", "Seed", "Alms", "Other"];
const PAYMENT_METHODS    = ["Cash", "Cheque", "E-Transfer", "Card"];
const EXPENSE_CATEGORIES = [
  "Mortgage/Rent", "Staff Wages", "Guest Speakers", "Equipment/Materials",
  "Pastor Stipend", "Utilities", "Legal Fees", "Bank Fees", "Benevolence",
  "Missionary Support", "Staff Travel", "Ministry Education", "Licenses",
  "Insurances", "Payroll Taxes", "Other",
];

function todayISO() { return todayLocalISO(); }

function dollarsToCents(v) {
  const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

function centsToDollarsStr(c, symbol) {
  return `${symbol}${((Number(c) || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function sortMembers(members) {
  return [...(members || [])]
    .filter(m => m.status !== "inactive" && !m.archived)
    .sort((a, b) => {
      const la = (a.lastName || "").toLowerCase();
      const lb = (b.lastName || "").toLowerCase();
      if (la !== lb) return la < lb ? -1 : 1;
      const fa = (a.firstName || "").toLowerCase();
      const fb = (b.firstName || "").toLowerCase();
      return fa < fb ? -1 : fa > fb ? 1 : 0;
    });
}

function uid() { return `row_${Math.random().toString(36).slice(2, 9)}`; }

export default function BulkEntryModal({
  open,
  members,
  currencySymbol = "$",
  initialMode = "giving",
  onClose,
  onSaved,
}) {
  const orderedMembers = useMemo(() => sortMembers(members), [members]);

  const [mode, setMode] = useState(initialMode);           // "giving" | "expenses"
  const [date, setDate] = useState(todayISO());
  const [paymentMethod, setPaymentMethod] = useState("E-Transfer");

  // Giving state — keyed by memberId + "__anon".
  const [givingRows, setGivingRows] = useState({});
  const [quickFillAmt, setQuickFillAmt] = useState("");

  // Expense state — ordered array of { rowId, payee, amount, category }.
  const [expenseRows, setExpenseRows] = useState([
    { rowId: uid(), payee: "", amount: "", category: "Utilities" },
    { rowId: uid(), payee: "", amount: "", category: "Utilities" },
    { rowId: uid(), payee: "", amount: "", category: "Utilities" },
  ]);
  const [payeeSuggestions, setPayeeSuggestions] = useState([]);
  const [payeeFocusRow, setPayeeFocusRow] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Load payee autocomplete once when the modal opens (used by expense mode).
    apiListPayees()
      .then(list => setPayeeSuggestions(Array.isArray(list) ? list : []))
      .catch(() => setPayeeSuggestions([]));
  }, [open]);

  const totalCents = useMemo(() => {
    if (mode === "giving") {
      let sum = 0;
      Object.values(givingRows).forEach(r => { sum += dollarsToCents(r?.amount || ""); });
      return sum;
    }
    return expenseRows.reduce((s, r) => s + dollarsToCents(r.amount || ""), 0);
  }, [mode, givingRows, expenseRows]);

  const nonZeroCount = useMemo(() => {
    if (mode === "giving") {
      return Object.values(givingRows).filter(r => dollarsToCents(r?.amount || "") > 0).length;
    }
    return expenseRows.filter(r => dollarsToCents(r.amount || "") > 0 && (r.payee || "").trim() !== "").length;
  }, [mode, givingRows, expenseRows]);

  if (!open) return null;

  // ---- Giving helpers ----
  const setGivingAmt = (key, amount) => {
    setGivingRows(prev => ({ ...prev, [key]: { ...(prev[key] || { category: "Regular Offering" }), amount } }));
  };
  const setGivingCat = (key, category) => {
    setGivingRows(prev => ({ ...prev, [key]: { ...(prev[key] || { amount: "" }), category } }));
  };
  const applyQuickFill = () => {
    const amt = String(quickFillAmt || "").trim();
    if (amt === "" || dollarsToCents(amt) <= 0) return;
    setGivingRows(prev => {
      const next = { ...prev };
      orderedMembers.forEach(m => {
        next[m.id] = { amount: amt, category: prev[m.id]?.category || "Regular Offering" };
      });
      return next;
    });
  };

  // ---- Expense helpers ----
  const updateExpenseRow = (rowId, patch) => {
    setExpenseRows(prev => prev.map(r => r.rowId === rowId ? { ...r, ...patch } : r));
  };
  const addExpenseRow = () => {
    setExpenseRows(prev => [...prev, { rowId: uid(), payee: "", amount: "", category: "Utilities" }]);
  };
  const removeExpenseRow = (rowId) => {
    setExpenseRows(prev => prev.length > 1 ? prev.filter(r => r.rowId !== rowId) : prev);
  };

  const clearAll = () => {
    if (mode === "giving") {
      setGivingRows({});
      setQuickFillAmt("");
    } else {
      setExpenseRows([{ rowId: uid(), payee: "", amount: "", category: "Utilities" }]);
    }
    setError("");
  };

  const canSubmit = !submitting && nonZeroCount > 0 && !summary && Boolean(date);

  const submit = async () => {
    setError("");
    setSubmitting(true);
    const successes = [];
    const failures = [];
    try {
      if (mode === "giving") {
        for (const [key, row] of Object.entries(givingRows)) {
          const cents = dollarsToCents(row?.amount || "");
          if (cents <= 0) continue;
          const isAnon = key === "__anon";
          try {
            await apiCreateGiving({
              isAnonymous: isAnon,
              memberId:    isAnon ? null : key,
              date,
              amountCents: cents,
              category:    row.category || "Regular Offering",
              paymentMethod,
            });
            successes.push(cents);
          } catch (e) { failures.push({ key, msg: e?.message || "Save failed" }); }
        }
      } else {
        for (const row of expenseRows) {
          const cents = dollarsToCents(row.amount || "");
          const payee = (row.payee || "").trim();
          if (cents <= 0 || !payee) continue;
          const category = row.category || "Utilities";
          try {
            await apiCreateExpense({
              payeeName: payee,
              payeeType: "Vendor",
              date,
              amountCents: cents,
              category,
              // category "Other" is not in the default bulk list, but if a
              // user manually types "Other" in the future we still ship a
              // placeholder note so backend validation passes.
              categoryOther: category === "Other" ? "Other" : null,
              notes: null,
            });
            successes.push(cents);
          } catch (e) { failures.push({ key: row.rowId, msg: e?.message || "Save failed" }); }
        }
      }
    } finally {
      setSubmitting(false);
    }
    if (failures.length > 0 && successes.length === 0) {
      setError(`All ${failures.length} rows failed to save. ${failures[0].msg}`);
      return;
    }
    setSummary({
      created: successes.length,
      totalCents: successes.reduce((a, b) => a + b, 0),
      failed: failures.length,
    });
    onSaved && onSaved();
  };

  const closeAndReset = () => {
    setGivingRows({});
    setExpenseRows([{ rowId: uid(), payee: "", amount: "", category: "Utilities" }]);
    setSummary(null);
    setError("");
    setQuickFillAmt("");
    onClose && onClose();
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && closeAndReset()}
      data-testid="finance-bulk-entry-modal"
      style={{ alignItems: "flex-start", paddingTop: 12, paddingBottom: 84 }}
    >
      <div
        className="modal"
        style={{
          maxWidth: 620,
          width: "min(96vw, 620px)",
          maxHeight: "calc(100vh - 96px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div
          className="modal-header"
          style={{ position: "sticky", top: 0, background: "inherit", zIndex: 2 }}
        >
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <CustomIcon name="coin" size={18} /> Bulk Entry
          </h3>
          <button
            className="modal-close"
            onClick={closeAndReset}
            data-testid="finance-bulk-close"
          >×</button>
        </div>

        {!summary && (
          <>
            {/* MODE TOGGLE */}
            <div
              style={{
                padding: "10px 16px 0",
                background: "rgba(0,0,0,0.35)",
              }}
            >
              <div
                role="tablist"
                style={{
                  display: "flex",
                  background: "color-mix(in srgb, var(--kc-text) 6%, transparent)",
                  padding: 4,
                  borderRadius: 10,
                  gap: 4,
                }}
              >
                <ModeTab
                  active={mode === "giving"}
                  onClick={() => setMode("giving")}
                  label="Giving"
                  testid="finance-bulk-mode-giving"
                />
                <ModeTab
                  active={mode === "expenses"}
                  onClick={() => setMode("expenses")}
                  label="Expenses"
                  testid="finance-bulk-mode-expenses"
                />
              </div>
            </div>

            {/* TOP CONTROLS */}
            <div
              style={{
                padding: "12px 16px",
                borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)",
                background: "rgba(0,0,0,0.35)",
                position: "sticky", top: 52, zIndex: 2,
              }}
            >
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                <div style={{ flex: "1 1 160px", minWidth: 140 }}>
                  <label className="form-label" style={{ fontSize: 11 }}>Date (batch)</label>
                  <input
                    type="date"
                    className="form-input"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="finance-bulk-date"
                  />
                </div>
                {mode === "giving" && (
                  <div style={{ flex: "1 1 160px", minWidth: 140 }}>
                    <label className="form-label" style={{ fontSize: 11 }}>Payment Method (batch)</label>
                    <select
                      className="form-input"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      data-testid="finance-bulk-payment-method"
                    >
                      {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                )}
              </div>
              {/* Running total */}
              <div
                data-testid="finance-bulk-total-card"
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: "color-mix(in srgb, var(--kc-accent) 12%, transparent)",
                  border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>
                    Batch Total · {mode === "giving" ? "Giving" : "Expenses"}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.6 }} data-testid="finance-bulk-nonzero-count">
                    {nonZeroCount} {nonZeroCount === 1 ? "entry" : "entries"} with amount
                  </div>
                </div>
                <div
                  data-testid="finance-bulk-total"
                  style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-accent)" }}
                >
                  {centsToDollarsStr(totalCents, currencySymbol)}
                </div>
              </div>
            </div>
          </>
        )}

        {/* SCROLL AREA */}
        {!summary && (
          <div
            style={{ overflowY: "auto", flex: 1, padding: "8px 8px 12px" }}
            data-testid="finance-bulk-list"
          >
            {mode === "giving" ? (
              <>
                {/* Quick-fill */}
                <div
                  data-testid="finance-bulk-quickfill-row"
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "10px 8px", marginBottom: 6,
                    background: "color-mix(in srgb, var(--kc-accent) 8%, transparent)",
                    border: "1px solid color-mix(in srgb, var(--kc-accent) 28%, transparent)",
                    borderRadius: 8,
                  }}
                >
                  <div style={{ flex: "1 1 140px", minWidth: 0, fontSize: 12, fontWeight: 600, color: "var(--kc-text)" }}>
                    Apply same amount<br/>
                    <span style={{ fontSize: 10, fontWeight: 500, opacity: 0.6 }}>to all members</span>
                  </div>
                  <div style={{ position: "relative", flex: "0 0 110px" }}>
                    <span aria-hidden="true" style={{
                      position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                      color: "var(--kc-meta)", pointerEvents: "none", fontSize: 13,
                    }}>{currencySymbol}</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      className="form-input"
                      value={quickFillAmt}
                      onChange={(e) => setQuickFillAmt(e.target.value)}
                      data-testid="finance-bulk-quickfill-amt"
                      style={{ paddingLeft: 20, textAlign: "right", fontFamily: "monospace" }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={applyQuickFill}
                    disabled={dollarsToCents(quickFillAmt) <= 0}
                    data-testid="finance-bulk-quickfill-apply"
                    style={{
                      flex: "0 0 auto",
                      background: dollarsToCents(quickFillAmt) > 0 ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 35%, transparent)",
                      color: "#111", fontWeight: 800,
                      padding: "8px 16px",
                      minHeight: 38,
                      borderRadius: 8,
                      border: "none",
                      cursor: dollarsToCents(quickFillAmt) > 0 ? "pointer" : "not-allowed",
                      fontSize: 12, letterSpacing: 0.4, textTransform: "uppercase",
                    }}
                  >Apply</button>
                </div>
                {orderedMembers.map(m => (
                  <GivingRow
                    key={m.id}
                    testid={`finance-bulk-row-${m.id}`}
                    left={<Avatar name={`${m.firstName} ${m.lastName}`} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{ width: 36, height: 36, fontSize: 14 }} />}
                    label={`${m.lastName || ""}, ${m.firstName || ""}`.replace(/^, |, $/g, "")}
                    amount={givingRows[m.id]?.amount || ""}
                    onAmount={(v) => setGivingAmt(m.id, v)}
                    category={givingRows[m.id]?.category || "Regular Offering"}
                    onCategory={(v) => setGivingCat(m.id, v)}
                    currencySymbol={currencySymbol}
                    keyName={m.id}
                  />
                ))}
                {/* Pinned Anonymous row */}
                <div
                  style={{
                    marginTop: 6,
                    borderTop: "1px dashed color-mix(in srgb, var(--kc-accent) 30%, transparent)",
                    paddingTop: 8,
                  }}
                  data-testid="finance-bulk-anon-wrap"
                >
                  <GivingRow
                    testid="finance-bulk-row-anon"
                    left={
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: "color-mix(in srgb, var(--kc-accent) 15%, transparent)",
                        border: "1px dashed var(--kc-accent)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "var(--kc-accent)", fontSize: 12, fontWeight: 700,
                      }}>?</div>
                    }
                    label={<span style={{ fontStyle: "italic" }}>Anonymous</span>}
                    amount={givingRows.__anon?.amount || ""}
                    onAmount={(v) => setGivingAmt("__anon", v)}
                    category={givingRows.__anon?.category || "Regular Offering"}
                    onCategory={(v) => setGivingCat("__anon", v)}
                    currencySymbol={currencySymbol}
                    keyName="anon"
                  />
                </div>
              </>
            ) : (
              <>
                {expenseRows.map((row, idx) => (
                  <ExpenseRow
                    key={row.rowId}
                    idx={idx}
                    row={row}
                    onChange={(patch) => updateExpenseRow(row.rowId, patch)}
                    onRemove={() => removeExpenseRow(row.rowId)}
                    canRemove={expenseRows.length > 1}
                    currencySymbol={currencySymbol}
                    payeeSuggestions={payeeSuggestions}
                    hasFocus={payeeFocusRow === row.rowId}
                    setFocus={(v) => setPayeeFocusRow(v ? row.rowId : null)}
                  />
                ))}
                <button
                  type="button"
                  onClick={addExpenseRow}
                  data-testid="finance-bulk-expense-add-row"
                  style={{
                    marginTop: 10,
                    padding: "10px 14px",
                    borderRadius: 8,
                    background: "color-mix(in srgb, var(--kc-accent) 12%, transparent)",
                    border: "1px dashed var(--kc-accent)",
                    color: "var(--kc-accent)",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    width: "100%",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}
                >
                  <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Row
                </button>
              </>
            )}
          </div>
        )}

        {/* SUCCESS SUMMARY */}
        {summary && (
          <div style={{ padding: 24, textAlign: "center" }} data-testid="finance-bulk-summary">
            <div style={{ fontSize: 48, color: "#4ade80" }}>✓</div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 20, fontWeight: 700, marginTop: 8 }}>
              Batch Saved
            </div>
            <div style={{ fontSize: 14, opacity: 0.85, marginTop: 8 }}>
              <b data-testid="finance-bulk-summary-count">{summary.created}</b>{" "}
              {mode === "giving" ? "giving" : "expense"}{" "}
              {summary.created === 1 ? "record" : "records"} created — total{" "}
              <b style={{ color: "var(--kc-accent)" }} data-testid="finance-bulk-summary-total">
                {centsToDollarsStr(summary.totalCents, currencySymbol)}
              </b>.
              {summary.failed > 0 && (
                <div style={{ color: "#f87171", marginTop: 8 }}>
                  {summary.failed} row{summary.failed === 1 ? "" : "s"} failed to save.
                </div>
              )}
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div
          style={{
            position: "sticky", bottom: 0, background: "var(--kc-surface)",
            borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 22%, transparent)",
            padding: "12px 16px",
            display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
          }}
        >
          {error && !summary && (
            <div
              style={{ color: "#f87171", fontSize: 12, flexBasis: "100%" }}
              data-testid="finance-bulk-error"
            >{error}</div>
          )}
          {!summary ? (
            <>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={clearAll}
                data-testid="finance-bulk-clear"
              >Clear All</button>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                className="btn"
                disabled={!canSubmit}
                onClick={submit}
                data-testid="finance-bulk-submit"
                style={{
                  background: canSubmit ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 35%, transparent)",
                  color: "#111",
                  fontWeight: 800,
                  minHeight: 44,
                  padding: "0 22px",
                  borderRadius: 10,
                  cursor: canSubmit ? "pointer" : "not-allowed",
                }}
              >
                {submitting ? "Saving…" : `Submit Batch (${nonZeroCount})`}
              </button>
            </>
          ) : (
            <button
              className="btn btn-primary"
              onClick={closeAndReset}
              style={{ marginLeft: "auto" }}
              data-testid="finance-bulk-done"
            >Done</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ------- ModeTab -------
function ModeTab({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      data-testid={testid}
      style={{
        flex: 1,
        padding: "8px 14px",
        borderRadius: 8,
        border: "none",
        background: active ? "var(--kc-accent)" : "transparent",
        color: active ? "#111" : "var(--kc-text)",
        fontWeight: 800,
        fontSize: 13,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        cursor: "pointer",
      }}
    >{label}</button>
  );
}

// ------- GivingRow -------
function GivingRow({ testid, left, label, amount, onAmount, category, onCategory, currencySymbol, keyName }) {
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 8px",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
      }}
    >
      {left}
      <div style={{ flex: "1 1 140px", minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {label}
        </div>
      </div>
      <div style={{ position: "relative", flex: "0 0 110px" }}>
        <span aria-hidden="true" style={{
          position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
          color: "var(--kc-meta)", pointerEvents: "none", fontSize: 13,
        }}>{currencySymbol}</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder="0.00"
          className="form-input"
          value={amount}
          onChange={(e) => onAmount(e.target.value)}
          data-testid={`finance-bulk-amt-${keyName}`}
          style={{ paddingLeft: 20, textAlign: "right", fontFamily: "monospace" }}
        />
      </div>
      <select
        className="form-input"
        value={category}
        onChange={(e) => onCategory(e.target.value)}
        data-testid={`finance-bulk-cat-${keyName}`}
        style={{ flex: "0 0 130px", fontSize: 12 }}
      >
        {GIVING_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  );
}

// ------- ExpenseRow -------
function ExpenseRow({ idx, row, onChange, onRemove, canRemove, currencySymbol, payeeSuggestions, hasFocus, setFocus }) {
  // Filter suggestions by current typed input.
  const q = (row.payee || "").trim().toLowerCase();
  const matches = q === ""
    ? payeeSuggestions.slice(0, 8)
    : payeeSuggestions.filter(p => (p.payeeName || "").toLowerCase().includes(q)).slice(0, 8);

  return (
    <div
      data-testid={`finance-bulk-expense-row-${idx}`}
      style={{
        display: "flex", alignItems: "flex-start", gap: 6,
        padding: "8px 6px",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
      }}
    >
      {/* Payee + autocomplete */}
      <div style={{ position: "relative", flex: "1 1 180px", minWidth: 130 }}>
        <input
          type="text"
          className="form-input"
          placeholder="Payee name"
          value={row.payee}
          onChange={(e) => onChange({ payee: e.target.value })}
          onFocus={() => setFocus(true)}
          onBlur={() => setTimeout(() => setFocus(false), 150)}
          data-testid={`finance-bulk-expense-payee-${idx}`}
        />
        {hasFocus && matches.length > 0 && (
          <div
            data-testid={`finance-bulk-expense-payee-suggestions-${idx}`}
            style={{
              position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
              background: "var(--kc-surface)",
              border: "1px solid color-mix(in srgb, var(--kc-accent) 40%, transparent)",
              borderRadius: 8,
              marginTop: 4,
              maxHeight: 160,
              overflowY: "auto",
              boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
            }}
          >
            {matches.map(m => (
              <button
                key={m.payeeName}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onChange({ payee: m.payeeName }); setFocus(false); }}
                data-testid={`finance-bulk-expense-payee-suggest-${idx}-${m.payeeName}`}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  background: "transparent",
                  border: "none",
                  color: "var(--kc-text)",
                  cursor: "pointer",
                  fontSize: 13,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "color-mix(in srgb, var(--kc-accent) 10%, transparent)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {m.payeeName}
                {m.payeeType && (
                  <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.6 }}>· {m.payeeType}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Amount */}
      <div style={{ position: "relative", flex: "0 0 100px" }}>
        <span aria-hidden="true" style={{
          position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
          color: "var(--kc-meta)", pointerEvents: "none", fontSize: 12,
        }}>{currencySymbol}</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder="0.00"
          className="form-input"
          value={row.amount}
          onChange={(e) => onChange({ amount: e.target.value })}
          data-testid={`finance-bulk-expense-amount-${idx}`}
          style={{ paddingLeft: 18, textAlign: "right", fontFamily: "monospace" }}
        />
      </div>
      {/* Category */}
      <select
        className="form-input"
        value={row.category}
        onChange={(e) => onChange({ category: e.target.value })}
        data-testid={`finance-bulk-expense-cat-${idx}`}
        style={{ flex: "0 0 140px", fontSize: 12 }}
      >
        {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      {/* Remove */}
      <button
        type="button"
        onClick={onRemove}
        disabled={!canRemove}
        data-testid={`finance-bulk-expense-remove-${idx}`}
        aria-label="Remove row"
        title={canRemove ? "Remove row" : "At least one row is required"}
        style={{
          flex: "0 0 auto",
          background: "transparent",
          border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
          color: canRemove ? "#f87171" : "color-mix(in srgb, var(--kc-text) 20%, transparent)",
          borderRadius: 8,
          width: 34, height: 36,
          cursor: canRemove ? "pointer" : "not-allowed",
          fontWeight: 800,
          fontSize: 15,
        }}
      >×</button>
    </div>
  );
}
